import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DragAndDropOverlay } from './DragAndDropOverlay.web';
import { ImagePreviewSheet } from '@/components/ui/image/ImagePreviewSheet';
import { resizeImageForUpload } from '@/components/ui/image/resizeImageForUpload';

export interface IWebDropImageUploadProps {
  isEnabled?: boolean;
  overlayLabel?: string;
  maxSizeBytes?: number; // default 16MB
  title?: string; // preview sheet title
  caption?: string;
  onCaptionChange?: (text: string) => void;
  isUploading?: boolean;
  onUpload: (file: File) => Promise<void> | void;
  showCaption?: boolean;
  showPolicyNotice?: boolean;
}

/**
 * Web-only high-level component that listens for file drag-and-drop globally,
 * shows an overlay, opens a preview sheet for the dropped image, and invokes onUpload on confirm.
 */
export function WebDropImageUpload({
  isEnabled = true,
  overlayLabel,
  maxSizeBytes = 16 * 1024 * 1024,
  title,
  caption,
  onCaptionChange,
  isUploading,
  onUpload,
  showCaption = true,
  showPolicyNotice,
}: IWebDropImageUploadProps) {
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [previewUri, setPreviewUri] = useState<string | null>(null);

  const handleFilesDropped = useCallback(
    async (files: File[]) => {
      if (!isEnabled) return;
      if (!files || files.length === 0) return;
      const droppedImage = files.find((f) => f.type?.startsWith('image/')) || null;
      if (!droppedImage) return;
      const imageFile = await resizeImageForUpload(droppedImage);
      if (imageFile === droppedImage && imageFile.size > maxSizeBytes) return;
      try {
        const url = URL.createObjectURL(imageFile);
        setDroppedFile(imageFile);
        setPreviewUri(url);
      } catch {}
    },
    [isEnabled, maxSizeBytes]
  );

  const closePreview = useCallback(() => {
    try {
      if (previewUri) URL.revokeObjectURL(previewUri);
    } catch {}
    setPreviewUri(null);
    setDroppedFile(null);
  }, [previewUri]);

  const confirmUpload = useCallback(async () => {
    if (!droppedFile) return;
    try {
      await onUpload(droppedFile);
      // Close preview after successful upload
      closePreview();
    } catch {
      // Leave preview open on error; parent should show error toast
    }
  }, [droppedFile, onUpload, closePreview]);

  if (!isEnabled) return null;

  return (
    <>
      <DragAndDropOverlay isEnabled={isEnabled} label={overlayLabel} onDropFiles={handleFilesDropped} />
      {previewUri ? (
        <ImagePreviewSheet
          imageUri={previewUri}
          onConfirm={confirmUpload}
          onCancel={closePreview}
          isUploading={!!isUploading}
          title={title}
          caption={caption}
          onCaptionChange={onCaptionChange}
          showCaption={showCaption}
          showPolicyNotice={showPolicyNotice}
        />
      ) : null}
    </>
  );
}

export default WebDropImageUpload;


